import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LearningOutcomeSelector from './LearningOutcomeSelector';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import SearchableDropdown from '../common/SearchableDropdown';

const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setFocusedIndex(-1);
    }, [search, isOpen]);

    useEffect(() => {
        if (isOpen && focusedIndex >= 0) {
            const el = document.getElementById(`multi-dropdown-option-${focusedIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [focusedIndex, isOpen]);

    const values = Array.isArray(selectedValues) ? selectedValues : [];

    const toggleOption = (optionId) => {
        const isSelected = values.includes(optionId);
        const newValues = isSelected
            ? values.filter(id => id !== optionId)
            : [...values, optionId];
        onChange(newValues);
    };

    const filteredOptions = (options || []).filter(opt =>
        opt.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        const optionsCount = filteredOptions.length;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex(prev => (prev < optionsCount - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                toggleOption(filteredOptions[focusedIndex].id);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative w-full ${isOpen ? 'z-[90]' : 'z-10'}`} ref={containerRef} onKeyDown={handleKeyDown}>
            <div
                tabIndex={0}
                onClick={() => setIsOpen(!isOpen)}
                className={`min-h-[56px] px-5 py-3 rounded-2xl cursor-pointer transition-all flex flex-wrap gap-2 items-center border-2 focus:outline-none ${isOpen ? 'border-[#18216D] bg-white' : 'border-slate-200 hover:border-[#18216D]/30'} ${selectedValues?.length === 0 ? 'bg-slate-100' : 'bg-slate-50'}`}
            >
                {values.length > 0 ? (
                    values.map(id => {
                        const opt = options.find(o => o.id === id);
                        return (
                            <span key={id} className="px-3 py-1 bg-[#18216D] text-white text-[10px] font-black uppercase tracking-tight rounded-lg flex items-center gap-2">
                                {opt?.name || id}
                                <XMarkIcon
                                    className="w-3 h-3 cursor-pointer hover:text-red-300"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleOption(id);
                                    }}
                                />
                            </span>
                        );
                    })
                ) : (
                    <span className="text-slate-400 text-sm font-bold">{placeholder}</span>
                )}
            </div>

            {isOpen && (
                <div className="absolute z-[100] mt-2 w-full bg-white border border-slate-100 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
                    <div className="p-3 border-b border-slate-50 bg-white sticky top-0 z-10 shrink-0">
                        <input
                            autoFocus
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search strands..."
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 transition-all"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto custom-scrollbar flex flex-col relative z-0">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => {
                                const isFocused = focusedIndex === idx;
                                const isSelected = values.includes(opt.id);
                                return (
                                    <div
                                        key={opt.id}
                                        id={`multi-dropdown-option-${idx}`}
                                        onClick={() => toggleOption(opt.id)}
                                        className={`px-5 py-4 cursor-pointer transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between ${isFocused ? 'bg-indigo-100/50' : (isSelected ? 'bg-indigo-50' : 'hover:bg-indigo-50/50')}`}
                                    >
                                        <span className="text-sm font-black text-[#18216D]">{opt.name}</span>
                                        {isSelected && <CheckCircleIcon className="w-5 h-5 text-indigo-600" />}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-5 py-8 text-center">
                                <span className="font-bold text-slate-400 text-xs uppercase tracking-widest">No matches found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


// ---------------------------------------------------------------------------
// Strand marks and band minimums
// Every minimum comes from the school's performance bands (Admin > Performance Standards).
// Nothing here is hardcoded: until the ranges are loaded no minimum is produced.
// ---------------------------------------------------------------------------
const BAND_LEVELS = ['EE', 'ME', 'AE', 'BE']; // highest to lowest

// Minimum percentage to be in each band at all: the lowest minimum among its sub-bands (EE1, EE2 -> EE).
// Returns null unless all four bands are defined.
const bandRangesFromBands = (bands) => {
    if (!Array.isArray(bands)) return null;
    const ranges = {};
    BAND_LEVELS.forEach(level => {
        const percents = bands
            .filter(b => String(b.band || '').toUpperCase().startsWith(level))
            .map(b => Number(b.min_percent))
            .filter(Number.isFinite);
        if (percents.length > 0) ranges[level] = Math.min(...percents);
    });
    return BAND_LEVELS.every(level => ranges[level] !== undefined) ? ranges : null;
};

// Smallest whole number of marks that reaches `minPercent` of `maxMarks`.
// The rounding step removes floating point noise (e.g. 30 * 10 / 100 = 3.0000000000000004).
const minimumMarksForBand = (minPercent, maxMarks) => {
    if (!(maxMarks > 0)) return 0;
    return Math.ceil(Math.round(maxMarks * minPercent * 1e4) / 1e6);
};

// The four band minimums for a strand max: the starting values of the Min EE / ME / AE / BE boxes
const bandMinimumsForMax = (ranges, maxMarks) => ({
    max: maxMarks,
    ee: minimumMarksForBand(ranges.EE, maxMarks),
    me: minimumMarksForBand(ranges.ME, maxMarks),
    ae: minimumMarksForBand(ranges.AE, maxMarks),
    be: minimumMarksForBand(ranges.BE, maxMarks)
});

// New assessment total: scale every selected strand's max so they add up to it exactly
const rescaleStrandMaxes = (strandIds, thresholds, newTotal, ranges) => {
    const maxes = strandIds.map(id => Number(thresholds[id]?.max) || 0);
    const sum = maxes.reduce((a, b) => a + b, 0);
    if (sum <= 0 || newTotal <= 0) return thresholds;

    const scaled = maxes.map(m => Math.round((m * newTotal) / sum));
    const difference = newTotal - scaled.reduce((a, b) => a + b, 0);
    if (difference !== 0) {
        const largest = maxes.indexOf(Math.max(...maxes));
        scaled[largest] = Math.max(0, scaled[largest] + difference);
    }

    const next = { ...thresholds };
    strandIds.forEach((id, i) => {
        // New strand max: the band minimums are recalculated from the ranges, never scaled from old values
        next[id] = bandMinimumsForMax(ranges, scaled[i]);
    });
    return next;
};

// A band mark input that keeps a typed draft but can never store a value below the calculated minimum
const BandInput = ({ level, value, floor, ceiling, orderNote, hasError, onCommit }) => {
    const [draft, setDraft] = useState(null);
    const shown = draft !== null ? draft : (value ?? '');

    const handleChange = (e) => {
        const raw = e.target.value;
        setDraft(raw);
        const parsed = parseInt(raw, 10);
        // A value inside the allowed range is stored straight away; anything else stays a draft until blur
        if (!isNaN(parsed) && parsed >= floor && parsed <= ceiling) {
            onCommit(parsed);
        }
    };

    const finish = () => {
        const parsed = parseInt(draft, 10);
        if (draft !== null) {
            const clamped = isNaN(parsed) ? floor : Math.min(Math.max(parsed, floor), ceiling);
            onCommit(clamped);
        }
        setDraft(null);
    };

    return (
        <div>
            <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1.5 ml-1">Min {level}</label>
            <input
                type="number"
                value={shown}
                onChange={handleChange}
                onBlur={finish}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finish(); } }}
                className={`w-full px-3 py-2 bg-white border-2 rounded-xl transition-all font-bold text-gray-900 outline-none text-xs ${hasError ? 'border-red-500 bg-red-50/30' : 'border-slate-200 focus:border-blue-600'}`}
                placeholder="Mark"
                min={floor}
                max={ceiling}
            />
            <span className="block text-[9px] font-bold text-slate-400 mt-1 ml-1">Min allowed: {floor}</span>
            {orderNote && (
                <span className="block text-[9px] font-bold text-red-600 mt-0.5 ml-1">{orderNote}</span>
            )}
        </div>
    );
};

const AssignmentCreator = ({ courseId, assignment, onClose, onSave }) => {
    const { get, post, patch } = useApi();
    const { showToast } = useAppState();
    const isEditMode = Boolean(assignment);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const [strands, setStrands] = useState([]);
    const [selectedStrandIds, setSelectedStrandIds] = useState(
        assignment?.tested_strands?.length > 0
            ? assignment.tested_strands
            : (assignment?.strand_id ? [assignment.strand_id] : [])
    );

    const [strandThresholds, setStrandThresholds] = useState(() => {
        let thresholds = assignment?.strand_thresholds || {};
        if (typeof thresholds === 'string') {
            try {
                thresholds = JSON.parse(thresholds);
                // Handle doubly stringified JSON just in case
                if (typeof thresholds === 'string') {
                    thresholds = JSON.parse(thresholds);
                }
            } catch (e) {
                console.error('Failed to parse strand_thresholds:', e);
                thresholds = {};
            }
        }
        return thresholds;
    });
    // Minimum percentage per band from the school's performance standards. No defaults:
    // stays null (and strand marks stay disabled) until the real ranges are loaded.
    const [bandRanges, setBandRanges] = useState(null);
    const [bandsStatus, setBandsStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

    const [templates, setTemplates] = useState([]);
    // While the teacher is typing a new summative total, strands are rescaled from this snapshot
    // (not from the previous keystroke) so rounding never compounds
    const totalEditBaseRef = React.useRef(null);
    const [selectedOutcomes, setSelectedOutcomes] = useState(
        assignment?.tested_outcomes_detail?.length > 0
            ? assignment.tested_outcomes_detail.map(o => ({
                id: o.id,
                description: o.description,
                code: o.code,
                sub_strand_id: o.sub_strand_id
            }))
            : (assignment?.learning_outcome ? [{
                id: parseInt(assignment.learning_outcome),
                description: assignment.learning_outcome_description,
                sub_strand_id: parseInt(assignment.sub_strand_id)
            }] : [])
    );

    const [formData, setFormData] = useState({
        title: assignment?.title || '',
        description: assignment?.description || '',
        due_date: assignment?.due_date ? assignment.due_date.split('T')[0] : '',
        due_time: assignment?.due_date ? assignment.due_date.split('T')[1]?.substring(0, 5) : '23:59',
        submission_types: assignment?.submission_types || ['file'],
        allow_late: assignment?.allow_late || false,
        late_penalty: assignment?.late_penalty || 0,
        assessment_type: assignment?.assessment_type || 'formative',
        exam_type: assignment?.exam_type || 'mid-term',
        criteria_ee: assignment?.criteria_ee || '',
        criteria_me: assignment?.criteria_me || '',
        criteria_ae: assignment?.criteria_ae || '',
        criteria_be: assignment?.criteria_be || '',
        is_manual: assignment?.submission_types?.length === 0 || assignment?.is_manual || false,
        total_marks: assignment?.total_marks || 20,
        rubric_templates: assignment?.rubric_templates?.length > 0 && typeof assignment.rubric_templates[0] === 'object' 
            ? assignment.rubric_templates.map(t => t.id)
            : (assignment?.rubric_templates || (assignment?.rubric_template ? [assignment.rubric_template.id || assignment.rubric_template] : [])),
        file: null
    });

    // Formative totals are locked to the sum of the strand max marks; summative totals are typed by the teacher
    const autoTotal = formData.assessment_type === 'formative';

    // Minimum marks for a band (EE / ME / AE / BE) out of a strand's max, from the school's ranges
    const minimumForBand = (level, maxMarks) => (bandRanges ? minimumMarksForBand(bandRanges[level], maxMarks) : 0);

    const sumOfStrandMaxes = (thresholdsMap, ids = selectedStrandIds) =>
        ids.reduce((sum, id) => sum + (Number(thresholdsMap[id]?.max) || 0), 0);

    // Teacher changes one strand's max marks: all four band minimums are recalculated from the ranges.
    // Summative: the assessment total becomes the new sum of the strand maxes.
    const handleStrandMaxChange = (strandId, newMax) => {
        if (!bandRanges) return;
        totalEditBaseRef.current = null;
        const next = { ...strandThresholds, [strandId]: bandMinimumsForMax(bandRanges, newMax) };
        setStrandThresholds(next);
        if (formData.assessment_type === 'summative') {
            setFormData(prev => ({ ...prev, total_marks: sumOfStrandMaxes(next) }));
        }
        if (errors.thresholds) {
            setErrors(prev => { const copy = { ...prev }; delete copy.thresholds; return copy; });
        }
    };

    // Teacher raises one band minimum. Exactly what they entered is stored; no other band is touched.
    const handleBandCommit = (strandId, level, value) => {
        setStrandThresholds(prev => {
            if (!prev[strandId]) return prev;
            return { ...prev, [strandId]: { ...prev[strandId], [level.toLowerCase()]: value } };
        });
        if (errors.thresholds) {
            setErrors(prev => { const copy = { ...prev }; delete copy.thresholds; return copy; });
        }
    };

    // Shown live (without changing any value) when a band is not above the band below it
    const bandOrderNote = (strandId, level) => {
        const lowerLevel = BAND_LEVELS[BAND_LEVELS.indexOf(level) + 1];
        if (!lowerLevel) return null;
        const current = strandThresholds[strandId] || {};
        const value = Number(current[level.toLowerCase()]);
        const lowerValue = Number(current[lowerLevel.toLowerCase()]);
        return value <= lowerValue ? `Must be higher than ${lowerLevel} (${lowerValue})` : null;
    };

    // Summative: teacher types the assessment total; the strand maxes rescale to add up to it
    const handleSummativeTotalChange = (e) => {
        if (!bandRanges) return;
        const parsed = parseInt(e.target.value, 10);
        const newTotal = isNaN(parsed) ? 0 : Math.max(0, parsed);
        if (!totalEditBaseRef.current) {
            totalEditBaseRef.current = { thresholds: strandThresholds, ids: selectedStrandIds };
        }
        setFormData(prev => ({ ...prev, total_marks: newTotal }));
        if (newTotal > 0) {
            const { thresholds, ids } = totalEditBaseRef.current;
            setStrandThresholds(rescaleStrandMaxes(ids, thresholds, newTotal, bandRanges));
        }
        if (errors.thresholds) {
            setErrors(prev => { const copy = { ...prev }; delete copy.thresholds; return copy; });
        }
    };

    // Selecting or removing strands: summative total follows the strands that remain
    const handleStrandSelection = (vals) => {
        totalEditBaseRef.current = null;
        setSelectedStrandIds(vals);
        if (formData.assessment_type === 'summative') {
            const sum = sumOfStrandMaxes(strandThresholds, vals);
            if (sum > 0) setFormData(prev => ({ ...prev, total_marks: sum }));
        }
        if (errors.learning_outcome) {
            setErrors(prev => {
                const next = { ...prev };
                delete next.learning_outcome;
                return next;
            });
        }
    };

    // Fetch current learning area, strands and performance bands
    useEffect(() => {
        const fetchInitialData = async () => {
            // The school's performance standards define every band minimum. No fallback values.
            try {
                const bandsData = await get('/api/performance-bands/');
                const ranges = bandRangesFromBands(bandsData);
                if (ranges) {
                    setBandRanges(ranges);
                    setBandsStatus('ready');
                } else {
                    setBandsStatus('error');
                }
            } catch (error) {
                console.error('Error fetching performance standards:', error);
                setBandsStatus('error');
            }

            try {
                if (courseId) {
                    const strandsList = await get(`/api/cbc/learning-areas/${courseId}/strands/`);
                    setStrands(Array.isArray(strandsList) ? strandsList : []);
                }
            } catch (error) {
                console.error('Error fetching curriculum data:', error);
                showToast('Failed to load curriculum context', 'error');
            }
        };
        fetchInitialData();
    }, [courseId, get, showToast]);

    // Fetch rubric templates
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                let data = [];
                if (courseId) {
                    data = await get(`/api/cbc/templates/by_learning_area/?learning_area_id=${courseId}`);
                } else {
                    data = await get('/api/cbc/templates/');
                }
                setTemplates(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };
        fetchTemplates();
    }, [get, courseId]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, file }));
        }
    };

    // Auto-calculate total marks for formative assessments based on strand MAX thresholds
    useEffect(() => {
        if (formData.assessment_type === 'formative') {
            const calculatedTotal = Object.values(strandThresholds).reduce((sum, thresholds) => {
                return sum + (Number(thresholds?.max) || 0);
            }, 0);
            if (calculatedTotal !== formData.total_marks) {
                setFormData(prev => ({ ...prev, total_marks: calculatedTotal }));
            }
        }
    }, [formData.assessment_type, strandThresholds, formData.total_marks]);


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };


    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Assignment title is required';
        if (!formData.is_manual && !formData.due_date) newErrors.due_date = 'Due date is required';
        if (selectedOutcomes.length === 0) newErrors.learning_outcome = 'At least one Learning Outcome is required';

        if (!formData.is_manual && formData.submission_types.length === 0) {
            newErrors.submission_types = 'Select at least one submission type';
        }

        // Threshold Validation
        const isSummative = formData.assessment_type === 'summative';
        // Summative: only the strands selected for this assessment matter
        let strandIdsToCheck = isSummative ? selectedStrandIds.map(String) : Object.keys(strandThresholds);
        if (!bandRanges) {
            // Without the school's performance standards no minimum can be checked
            newErrors.thresholds = 'Performance standards are not available for this school. Ask an administrator to set them up.';
            strandIdsToCheck = [];
        }

        strandIdsToCheck.forEach(strandId => {
            const levels = strandThresholds[strandId] || {};
            const strand = strands.find(s => s.id === parseInt(strandId));
            const strandName = strand?.name || 'Strand';
            const { max, ee, me, ae, be } = levels;
            const total = max || 0;

            if (total === 0) {
                newErrors.thresholds = isSummative
                    ? `${strandName}: Enter the marks this strand carries in the assessment`
                    : `${strandName}: Max marks must be greater than 0`;
                return;
            }

            if (ee > total || me > total || ae > total || be > total) {
                newErrors.thresholds = `${strandName}: Thresholds cannot exceed total marks (${total})`;
            }

            // Validate against Global Performance Standards
            const reqEE = minimumForBand('EE', total);
            const reqME = minimumForBand('ME', total);
            const reqAE = minimumForBand('AE', total);
            const reqBE = minimumForBand('BE', total);

            if (ee < reqEE || me < reqME || ae < reqAE || be < reqBE) {
                newErrors.thresholds = `${strandName}: Thresholds are below the school's minimum standards (Min EE: ${reqEE}, ME: ${reqME}, AE: ${reqAE}, BE: ${reqBE})`;
            }

            if (ee <= me || me <= ae || ae <= be) {
                newErrors.thresholds = `${strandName}: Thresholds must be descending (EE > ME > AE > BE)`;
            } else if (be < 0) {
                newErrors.thresholds = `${strandName}: Thresholds cannot be negative`;
            }
        });

        if (isSummative && bandRanges) {
            if (selectedStrandIds.length === 0) {
                newErrors.thresholds = 'Select at least one strand and enter its marks';
            } else if (!newErrors.thresholds) {
                // Safety net: strand marks and the assessment total must agree
                const allocated = sumOfStrandMaxes(strandThresholds);
                const assessmentTotal = parseInt(formData.total_marks, 10) || 0;
                if (allocated !== assessmentTotal) {
                    newErrors.thresholds = `Strand marks add up to ${allocated}, but the assessment total is ${assessmentTotal}. They must match.`;
                }
            }
        }

        return newErrors;
    };

    const getErrorSummary = () => {
        return null; // Global banner removed per USER_REQUEST
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // Show specific toasts for critical fields
            if (newErrors.title) showToast("The 'Assessment Title' is mandatory.", 'error');
            else if (newErrors.due_date) showToast("A 'Deadline Date' is required.", 'error');
            else if (newErrors.learning_outcome) showToast("At least one 'Learning Outcome' must be mapped.", 'error');
            else if (newErrors.thresholds) showToast(newErrors.thresholds, 'error');
            return;
        }

        setSubmitting(true);
        try {
            const formDataObj = new FormData();
            
            // Basic fields
            formDataObj.append('title', formData.title);
            formDataObj.append('description', formData.description);
            const finalDueDate = formData.due_date || new Date().toISOString().split('T')[0];
            const finalDueTime = formData.due_time || '23:59';
            formDataObj.append('due_date', `${finalDueDate}T${finalDueTime}:00`);
            formDataObj.append('status', 'Pending');
            formDataObj.append('is_cbc_assignment', 'true');
            formDataObj.append('assessment_type', formData.assessment_type);
            if (formData.assessment_type === 'summative') {
                formDataObj.append('exam_type', formData.exam_type);
            }
            formDataObj.append('is_manual', String(formData.is_manual));
            formDataObj.append('total_marks', String(parseInt(formData.total_marks) || 20));
            if (formData.assessment_type === 'formative') {
                formData.rubric_templates.forEach(id => formDataObj.append('rubric_templates', String(id)));
            }
            formDataObj.append('learning_area', courseId);
            if (selectedOutcomes[0]?.id) formDataObj.append('learning_outcome', String(selectedOutcomes[0].id));
            selectedOutcomes.forEach(o => formDataObj.append('tested_outcomes', String(o.id)));
            selectedStrandIds.forEach(id => formDataObj.append('tested_strands', String(id)));
            const thresholdsToSave = formData.assessment_type === 'summative'
                ? Object.fromEntries(selectedStrandIds.map(id => [id, strandThresholds[id]]).filter(([, v]) => v))
                : strandThresholds;
            formDataObj.append('strand_thresholds', JSON.stringify(thresholdsToSave));

            // Criteria
            formDataObj.append('criteria_ee', formData.criteria_ee);
            formDataObj.append('criteria_me', formData.criteria_me);
            formDataObj.append('criteria_ae', formData.criteria_ae);
            formDataObj.append('criteria_be', formData.criteria_be);

            // Submission types
            const types = formData.is_manual ? [] : formData.submission_types;
            formDataObj.append('submission_types', JSON.stringify(types));

            // File upload (Optional)
            if (formData.file) {
                formDataObj.append('file', formData.file);
            }

            const endpoint = '/api/assignments/';
            
            if (isEditMode) {
                await patch(`${endpoint}${assignment.id}/`, formDataObj, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showToast('Assessment updated successfully!');
            } else {
                await post(endpoint, formDataObj, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showToast('Assessment created successfully!');
            }

            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving assignment:', error);
            if (error.response?.data) {
                setErrors(error.response.data);
            } else {
                showToast('Failed to save assignment', 'error');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const modalContent = (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full flex flex-col animate-in fade-in zoom-in duration-300">
                    {/* Header */}
                <div className="px-8 py-5 border-b border-slate-50 flex items-center justify-between bg-white rounded-t-[2.5rem]">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-8 bg-[#18216D] rounded-full" />
                            <h2 className="text-xl font-black text-[#18216D] tracking-tight">
                                {isEditMode ? 'Edit Assessment' : 'New Assessment'}
                            </h2>
                        </div>
                        <span className="px-3 py-1 bg-[#18216D]/5 text-[#18216D] text-[9px] font-black uppercase tracking-[0.2em] rounded-xl border border-[#18216D]/10">
                            CBC Standard
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Global Error Banner */}
                {getErrorSummary() && (
                    <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                            <XMarkIcon className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-red-800 uppercase tracking-widest leading-none mb-1">Update Protocol Alert</p>
                            <p className="text-xs font-bold text-red-600">{getErrorSummary()}</p>
                        </div>
                    </div>
                )}

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="flex-1 p-10 space-y-10">

                    {/* Step 1: Evaluation Type */}
                    <section className="space-y-6">
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center text-sm">1</span>
                            <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs">Evaluation Type</h3>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setFormData(p => ({ ...p, assessment_type: 'formative' }))}
                                className={`flex-1 p-4 rounded-2xl border-2 transition-all text-center ${formData.assessment_type === 'formative' ? 'border-amber-500 bg-amber-50/50 text-amber-600' : 'border-slate-200 bg-white text-slate-600 hover:border-amber-500/30 hover:bg-slate-50'}`}
                            >
                                <p className="font-black text-xs uppercase">Formative Assessment</p>
                                <p className="text-[10px] font-bold opacity-70 mt-1">Ongoing</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(p => ({ ...p, assessment_type: 'summative' }))}
                                className={`flex-1 p-4 rounded-2xl border-2 transition-all text-center ${formData.assessment_type === 'summative' ? 'border-amber-500 bg-amber-50/50 text-amber-600' : 'border-slate-200 bg-white text-slate-600 hover:border-amber-500/30 hover:bg-slate-50'}`}
                            >
                                <p className="font-black text-xs uppercase">Summative Assessment</p>
                                <p className="text-[10px] font-bold opacity-70 mt-1">End of Term</p>
                            </button>
                        </div>
                        {formData.assessment_type === 'summative' && (
                            <div className="mt-4 p-4 bg-amber-50/50 border border-amber-100 rounded-2xl flex gap-2 animate-in fade-in slide-in-from-top-2">
                                <button
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, exam_type: 'mid-term' }))}
                                    className={`flex-1 p-3 rounded-xl border-2 transition-all text-center ${formData.exam_type === 'mid-term' ? 'border-amber-500 bg-white text-amber-600' : 'border-slate-200 bg-white text-slate-500 hover:border-amber-500/30'}`}
                                >
                                    <p className="font-black text-[10px] uppercase">Mid-Term Exam</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, exam_type: 'end-term' }))}
                                    className={`flex-1 p-3 rounded-xl border-2 transition-all text-center ${formData.exam_type === 'end-term' ? 'border-amber-500 bg-white text-amber-600' : 'border-slate-200 bg-white text-slate-500 hover:border-amber-500/30'}`}
                                >
                                    <p className="font-black text-[10px] uppercase">End-Term Exam</p>
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Step 2: Competency Selection */}
                    <section className="space-y-6">
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm">2</span>
                            <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs">Competency Selection</h3>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Select Strand</label>
                                <MultiSelectDropdown
                                    options={strands}
                                    selectedValues={selectedStrandIds}
                                    onChange={handleStrandSelection}
                                    placeholder="Select strand..."
                                />
                            </div>

                            {bandsStatus === 'error' && (
                                <p className="text-red-600 text-xs font-bold bg-red-50 p-3 rounded-xl border border-red-100">
                                    The school's performance standards could not be loaded or are incomplete (EE, ME, AE and BE must all be defined).
                                    Strand marks cannot be entered until an administrator sets them up under Performance Standards.
                                </p>
                            )}

                            {selectedStrandIds.map(strandId => {
                                const strand = strands.find(s => s.id === strandId);
                                if (!strand) return null;

                                return (
                                    <div key={strandId} className="p-6 bg-slate-50/50 rounded-[2rem] border border-slate-100 space-y-6 animate-in slide-in-from-top-4 duration-300">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-black text-[#18216D] text-xs uppercase tracking-widest flex items-center gap-2">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full" />
                                                {strand.name}
                                            </h4>
                                        </div>

                                        {/* Thresholds UI: same for formative and summative */}
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-[11px] font-black text-[#18216D] uppercase tracking-widest mb-1.5 ml-1">Max Marks for this Strand</label>
                                                <input
                                                    type="number"
                                                    value={strandThresholds[strandId]?.max || ''}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        handleStrandMaxChange(strandId, isNaN(val) ? 0 : Math.max(0, val));
                                                    }}
                                                    disabled={bandsStatus !== 'ready'}
                                                    className={`w-full max-w-[200px] px-3 py-3 bg-slate-100 border-2 rounded-xl transition-all font-bold text-gray-900 outline-none text-xs border-slate-200 focus:border-blue-600 disabled:opacity-60 disabled:cursor-not-allowed`}
                                                    placeholder={bandsStatus === 'loading' ? 'Loading performance standards...' : 'e.g. 10'}
                                                    min="0"
                                                />
                                            </div>

                                            {(strandThresholds[strandId]?.max > 0) && (
                                                <div className="grid grid-cols-4 gap-3 p-4 bg-blue-50/50 rounded-xl border border-blue-100/50">
                                                    {BAND_LEVELS.map(level => (
                                                        <BandInput
                                                            key={`${strandId}-${level}-${strandThresholds[strandId]?.max}`}
                                                            level={level}
                                                            value={strandThresholds[strandId]?.[level.toLowerCase()]}
                                                            floor={minimumForBand(level, strandThresholds[strandId]?.max)}
                                                            ceiling={Number(strandThresholds[strandId]?.max) || 0}
                                                            orderNote={bandOrderNote(strandId, level)}
                                                            hasError={Boolean(errors.thresholds?.includes(strand.name))}
                                                            onCommit={(v) => handleBandCommit(strandId, level, v)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <LearningOutcomeSelector
                                            learningAreaId={courseId}
                                            strandId={strandId}
                                            selectedOutcomes={selectedOutcomes}
                                            multiple={true}
                                            onChange={setSelectedOutcomes}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        {formData.assessment_type === 'summative' && selectedStrandIds.length > 0 && (() => {
                            const allocated = sumOfStrandMaxes(strandThresholds);
                            const assessmentTotal = parseInt(formData.total_marks, 10) || 0;
                            return (
                                <p className={`text-[11px] font-black uppercase tracking-widest mt-3 ml-1 ${allocated === assessmentTotal ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    Strand marks allocated: {allocated} / {assessmentTotal}
                                </p>
                            );
                        })()}
                        {errors.learning_outcome && <p className="text-red-500 text-xs font-bold mt-2 ml-1">{errors.learning_outcome}</p>}
                        {errors.thresholds && <p className="text-red-500 text-xs font-bold mt-2 ml-1 bg-red-50 p-3 rounded-xl border border-red-100">{errors.thresholds}</p>}
                    </section>


                    {/* Assessment Details */}
                    <section className="space-y-6">
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-sm">3</span>
                            <h3 className="font-black text-gray-900 uppercase tracking-widest text-xs">Assessment Details</h3>
                        </div>

                        <div className="space-y-4">
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Assessment Title (e.g. Weekly Math Quiz)"
                                className={`w-full px-5 py-4 bg-white border-2 rounded-2xl transition-all font-bold text-gray-900 outline-none ${errors.title ? 'border-red-500 bg-red-50/30' : 'border-slate-300 focus:border-[#18216D] hover:border-slate-400'}`}
                            />
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Describe the instructions for this competency task (Optional)..."
                                rows="4"
                                className={`w-full px-5 py-4 bg-white border-2 rounded-2xl transition-all font-bold text-gray-900 outline-none resize-none ${errors.description ? 'border-red-500 bg-red-50/30' : 'border-slate-300 focus:border-[#18216D] hover:border-slate-400'}`}
                            />


                            {/* File Upload Section (NEW) */}
                            <div className="p-6 bg-blue-50/30 border-2 border-dashed border-blue-100 rounded-[2rem] transition-all hover:border-blue-300">
                                <label className="block text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <i className="fas fa-file-pdf text-blue-600"></i>
                                    Upload Assessment Document (Optional)
                                </label>
                                <div className="flex items-center gap-4">
                                    <div className="flex-1 relative">
                                        <input
                                            type="file"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx"
                                            className="hidden"
                                            id="assignment-file"
                                        />
                                        <label
                                            htmlFor="assignment-file"
                                            className="flex items-center justify-between px-6 py-4 bg-white border border-blue-100 rounded-2xl cursor-pointer hover:shadow-lg transition-all group"
                                        >
                                            <span className={`text-sm font-bold ${(formData.file || assignment?.file) ? 'text-[#18216D]' : 'text-slate-400'}`}>
                                                {formData.file ? formData.file.name : (assignment?.file ? `Current: ${assignment.file.split('/').pop()}` : 'Select exam paper or instructions (PDF/Word)...')}
                                            </span>
                                            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                                                <i className={`fas ${formData.file ? 'fa-check' : 'fa-upload'}`}></i>
                                            </div>
                                        </label>
                                    </div>
                                    {formData.file && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, file: null }))}
                                            className="p-4 text-rose-500 hover:bg-rose-50 rounded-2xl transition-all"
                                            title="Remove file"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                {assignment?.file && !formData.file && (
                                    <div className="mt-3 ml-1 flex items-center gap-2">
                                        <i className="fas fa-link text-blue-400 text-[10px]"></i>
                                        <a href={assignment.file} target="_blank" rel="noreferrer" className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline decoration-2 underline-offset-4">
                                            View Currently Saved Document
                                        </a>
                                    </div>
                                )}
                                <p className="text-[10px] font-bold text-slate-400 mt-3 ml-1">
                                    This file will be available for students to download when they view the task.
                                </p>
                            </div>



                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
                                <div className="flex flex-col justify-start">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Total Marks / Score Cap</label>
                                    <input
                                        type="number"
                                        name="total_marks"
                                        value={formData.total_marks || ''}
                                        onChange={formData.assessment_type === 'summative' ? handleSummativeTotalChange : handleChange}
                                        onBlur={() => { totalEditBaseRef.current = null; }}
                                        disabled={autoTotal}
                                        placeholder="e.g. 50"
                                        min="0"
                                        className={`w-full px-5 py-4 border-2 transition-all font-bold text-gray-900 outline-none rounded-2xl ${autoTotal ? 'bg-slate-50 border-slate-300 text-slate-500 cursor-not-allowed opacity-70' : 'bg-white border-slate-300 focus:border-[#18216D] hover:border-slate-400'}`}
                                    />
                                    <p className="text-[10px] font-bold text-slate-400 italic mt-2 ml-1">
                                        {autoTotal
                                            ? 'Auto-calculated from strand max marks.' 
                                            : 'Type the exam total (e.g. 80 or 100). Strand marks adjust to add up to it.'}
                                    </p>
                                </div>
                                <div className="flex flex-col justify-start">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Submission Mode</label>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(p => ({ ...p, is_manual: !p.is_manual }))}
                                        className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${formData.is_manual ? 'border-amber-500 bg-amber-50/50' : 'border-slate-300 bg-white hover:border-slate-400'}`}
                                    >
                                        <div className="text-left">
                                            <p className={`font-black text-xs uppercase ${formData.is_manual ? 'text-amber-600' : 'text-slate-600'}`}>Manual Assessment</p>
                                            <p className={`text-[10px] font-bold opacity-70 ${formData.is_manual ? 'text-amber-700/70' : 'text-slate-500'}`}>No digital submission required (Physical work/Oral)</p>
                                        </div>
                                        <div className={`w-12 h-6 rounded-full p-1 transition-all ${formData.is_manual ? 'bg-amber-500' : 'bg-slate-200'}`}>
                                            <div className={`w-4 h-4 bg-white rounded-full transition-all transform ${formData.is_manual ? 'translate-x-6' : 'translate-x-0'}`} />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                        {/* Deadline Date (Hidden if Manual) */}
                        {!formData.is_manual ? (
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Deadline Date</label>
                                <input
                                    type="date"
                                    name="due_date"
                                    value={formData.due_date}
                                    onChange={handleChange}
                                    className={`w-full px-5 py-4 bg-slate-100 border-2 rounded-2xl transition-all font-bold text-gray-900 outline-none ${errors.due_date ? 'border-red-500 bg-red-50/30' : 'border-slate-200 focus:border-blue-600 focus:bg-white'}`}
                                />
                                <p className="text-[10px] font-bold text-slate-400 italic mt-2 ml-1">
                                    Scholars must submit before this date.
                                </p>
                            </div>
                        ) : (
                            <div className="hidden md:block"></div>
                        )}
                        
                        {/* Rubric Template Selector (Only visible if assessment_type is formative) */}
                        {formData.assessment_type === 'formative' && (
                            <div className="space-y-4 mt-2">
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Assessment Rubrics</label>
                                <SearchableDropdown 
                                    options={templates.filter(t => selectedStrandIds.includes(t.strand))} 
                                    selectedValues={formData.rubric_templates || []} 
                                    onChange={(val) => setFormData(p => {
                                        let newVals = [...(p.rubric_templates || [])];
                                        if (newVals.includes(val)) {
                                            newVals = newVals.filter(id => id !== val);
                                        } else {
                                            newVals.push(val);
                                        }
                                        return { ...p, rubric_templates: newVals };
                                    })} 
                                    placeholder="Select Rubrics..."
                                    isMultiple={true}
                                    displayKey="name"
                                    subDisplayKey="description"
                                />
                                <p className="text-[10px] font-bold text-slate-400 italic ml-1">
                                    Select predefined rubrics to evaluate the strands tested in this formative assessment.
                                </p>
                                {/* Selected Rubrics Vertical Tags */}
                                {formData.rubric_templates && formData.rubric_templates.length > 0 && (
                                    <div className="flex flex-col gap-2 mt-4">
                                        {formData.rubric_templates.map(id => {
                                            const template = templates.find(t => t.id === id);
                                            if (!template) return null;
                                            return (
                                                <div key={id} className="flex items-center justify-between p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl">
                                                    <div>
                                                        <p className="font-black text-[#18216D] text-xs">{template.name}</p>
                                                        <p className="text-[10px] font-bold text-indigo-400 mt-0.5 max-w-sm truncate">
                                                            {template.description ? (template.description.length > 50 ? template.description.substring(0, 50) + '...' : template.description) : 'No description available'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(p => ({
                                                            ...p,
                                                            rubric_templates: p.rubric_templates.filter(tid => tid !== id)
                                                        }))}
                                                        className="w-6 h-6 rounded-full bg-white border border-indigo-100 flex items-center justify-center text-indigo-400 hover:text-rose-500 hover:border-rose-200 transition-colors"
                                                    >
                                                        <XMarkIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-8 border-t border-slate-100 flex items-center justify-end gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-[#18216D] hover:bg-slate-50 rounded-2xl transition-all"
                        >
                            Discard
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={`px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] active:scale-95 transition-all flex items-center gap-3 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Syncing...</span>
                                </>
                            ) : (
                                <span>{isEditMode ? 'Update Task' : 'Publish Task'}</span>
                            )}
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default AssignmentCreator;
